import { useEffect, useRef } from "react";
import * as THREE from "three";
import vertexShader from "./shaders/vertex.glsl";
import fragmentShader from "./shaders/fragment.glsl";
import AnimationController from "./AnimationController";
import PropTypes from "prop-types";
import {
  createDisposerBag,
  ownWindowValue,
} from "@/features/tutor/vo-demo/imperative/disposers";

function AudioVisualizer({
  h,
  w,
  scale = 1,
  isEnded = false,
  currentScreen = "INTRO",
  initialState = 0,
  opacity = 1,
}) {
  const componentRef = useRef(null);
  const animationControllerRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    const disposers = createDisposerBag();
    const scaledW = w * scale;
    const scaledH = h * scale;
    const containerEl = componentRef.current;

    // Cap DPR: mobile gets 1.5x max (details invisible at 0.5 scale), desktop 2x
    const isMobile = window.innerWidth < 768;
    const dpr = Math.min(window.devicePixelRatio ?? 1, isMobile ? 1.5 : 2);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      scaledW / scaledH,
      0.1,
      1000,
    );
    const renderer = new THREE.WebGLRenderer({ antialias: !isMobile });
    renderer.setSize(scaledW, scaledH);
    renderer.setPixelRatio(dpr);

    if (containerEl) {
      containerEl.appendChild(renderer.domElement);
      disposers.add(() => {
        if (renderer.domElement.parentNode === containerEl) {
          try {
            containerEl.removeChild(renderer.domElement);
          } catch {
            // no-op
          }
        }
      });
    }

    camera.position.z = 100;

    const circleGeometry = new THREE.PlaneGeometry(scaledW, scaledH);
    // Ball sizes use a minimum of 0.75 scale so they stay visible on mobile (window scale 0.5)
    const ballScale = Math.max(scale, 1);
    const big_ball_size = 0.4 * ballScale;
    const small_ball_size = 0.2 * ballScale;
    const loading_radius = 0.08 * ballScale;
    const orbit_radius = 0.15 * ballScale;
    const circleMaterial = new THREE.ShaderMaterial({
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      uniforms: {
        u_resolution: {
          value: new THREE.Vector2(scaledW * dpr, scaledH * dpr),
        },
        u_time: { value: 0 },
        u_amplitude: { value: 0 },
        u_state: { value: 0 },
        u_state_start: { value: 0 },
        u_transition_type: { value: -1 },
        u_transition_start: { value: 0 },
        u_duration: { value: 0 },

        u_stop_session: { value: false },
        u_stop_time: { value: 0 },

        u_big_ball_size: { value: big_ball_size },
        u_small_ball_size: { value: small_ball_size },
        u_loading_radius: { value: loading_radius },
        u_orbit_radius: { value: orbit_radius },

        u_opacity: { value: opacity },
      },
    });

    const circleMesh = new THREE.Mesh(circleGeometry, circleMaterial);

    const animationController = new AnimationController(
      circleMaterial,
      currentScreen,
      initialState,
    );
    animationControllerRef.current = animationController;
    disposers.add(() => {
      if (animationControllerRef.current === animationController) {
        animationControllerRef.current = null;
      }
    });
    disposers.add(ownWindowValue("animationController", animationController));
    disposers.add(() => animationController.dispose());

    scene.add(camera);
    scene.add(circleMesh);
    disposers.add(() => {
      scene.remove(circleMesh);
      circleGeometry.dispose();
      circleMaterial.dispose();
      renderer.dispose();
      renderer.forceContextLoss();
    });

    const clock = new THREE.Clock();
    let smoothedAmp = 0;

    const animate = () => {
      if (disposers.disposed) return;
      animationFrameRef.current = requestAnimationFrame(animate);

      animationController.update();

      circleMaterial.uniforms.u_time.value = clock.getElapsedTime();

      const rawAmp = window.pcmPlayer?.getSmoothedAmplitude(10, 25) ?? 0;
      smoothedAmp += (rawAmp - smoothedAmp) * 0.1;
      circleMaterial.uniforms.u_amplitude.value = smoothedAmp;

      renderer.render(scene, camera);
    };

    animate();
    disposers.add(() => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    });

    return disposers.dispose;
  }, [w, h, scale, opacity, initialState, currentScreen]);

  useEffect(() => {
    if (isEnded && animationControllerRef.current) {
      animationControllerRef.current.end();
    }
  }, [isEnded]);

  return (
    <div
      ref={componentRef}
      style={{
        width: "fit-content",
        borderRadius: "16px",
        overflow: "hidden",
        backdropFilter: opacity === 1 ? "blur(50px)" : "none",
        boxShadow: opacity === 1 ? "0px 4px 4px 0px #00000040" : "none",
        position: "relative",
        zIndex: 1000,
      }}
    ></div>
  );
}

AudioVisualizer.propTypes = {
  h: PropTypes.number.isRequired,
  w: PropTypes.number.isRequired,
  scale: PropTypes.number,
  isEnded: PropTypes.bool,
  currentScreen: PropTypes.string,
  initialState: PropTypes.number,
  opacity: PropTypes.number,
};

export default AudioVisualizer;
